import { ScrollArea } from '@radix-ui/react-scroll-area'
import { capitalize } from 'lodash'
import MobileBackupLogCard from './MobileBackupLogCard'
import type { BackupAuditLog } from '../api/backupApi'
import { TBody, TD, TH, THead, TR, Table } from '@/components/custom/table'
import { formatDate } from '@/utils/timeZoneUtils'
import { useUsersStore } from '@/features/users/store/usersStore'
import { Container } from '@/components/Container'

type BackupHistoryProps = {
  logs: Array<BackupAuditLog>
  isLoading: boolean
}

export const BackupHistory = ({ logs, isLoading }: BackupHistoryProps) => {
  const users = useUsersStore((s) => s.users)

  if (isLoading) {
    return (
      <Container>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-700 mx-auto mb-4" />
      </Container>
    )
  }

  return (
    <div className="lg:space-y-2 space-y-1 flex-1">
      {/* Desktop Table */}
      <ScrollArea className="flex">
        <div className="hidden lg:block overflow-hidden rounded-md border bg-white w-full">
          <Table>
            <THead>
              <TR>
                <TH>Entity</TH>
                <TH>Date</TH>
                <TH>User</TH>
                <TH>Action</TH>
                <TH>Details</TH>
              </TR>
            </THead>
            <TBody>
              {logs.map((log) => {
                const logUser = users.find((u) => u._id === log.userId)
                const userName =
                  log.userId === '000000000000000000000000'
                    ? 'System'
                    : logUser
                      ? logUser.name
                      : 'Unknown User'

                return (
                  <TR key={log._id}>
                    <TD>{log.entity}</TD>
                    <TD>{formatDate(log.timestamp, 'DD/MM/YYYY hh:mm A')}</TD>
                    <TD>{userName}</TD>
                    <TD>{capitalize(log.action)}</TD>
                    <TD>
                      <pre
                        style={{
                          whiteSpace: 'pre-wrap',
                          wordWrap: 'break-word',
                          maxHeight: '200px',
                          overflowY: 'auto',
                        }}
                      >
                        {JSON.stringify(log.details, null, 2)}
                      </pre>
                    </TD>
                  </TR>
                )
              })}
              {logs.length === 0 && (
                <TR>
                  <TD className="p-6 text-center text-neutral-500" colSpan={6}>
                    No backups found
                  </TD>
                </TR>
              )}
            </TBody>
          </Table>
        </div>
      </ScrollArea>

      {/* Mobile Card List */}
      <div className="block lg:hidden space-y-4 overflow-hidden">
        {logs.length === 0 ? (
          <div className="p-6 text-center text-neutral-500 border rounded-md bg-white">
            No results
          </div>
        ) : (
          <ScrollArea className="flex h-full">
            <div className="flex-1 space-y-2 pb-4">
              {logs.map((log) => {
                return <MobileBackupLogCard key={log._id} log={log} />
              })}
            </div>
          </ScrollArea>
        )}
      </div>
    </div>
  )
}
export default BackupHistory
